import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Subject, Subscription, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { WalletBalance, WalletError } from 'src/app/models/Wallet';
import { classifyWalletError, WalletService } from 'src/app/shared/services/wallet.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import {
  MONEY_BALANCE_FRACTION_DIGITS,
  MONEY_DEFAULT_LOCALE,
} from 'src/app/shared/pipes/money-micros.pipe';
import {
  AccountVersion,
  AccountVersionService,
  INTERNATIONAL,
  NATIONAL,
} from 'src/app/shared/services/account-version.service';

/**
 * Carteira resumida no header (CU-86akkmeaz).
 *
 * O ícone vive no header, o modal mora dentro dele e a abertura é um `Subject` empurrado pelo pai — `ng-template` +
 * `@ViewChild` + `NgbModal`.
 *
 * A consulta só acontece na abertura. Buscar o saldo no `ngOnInit` custaria uma
 * requisição em toda navegação e hoje ela voltaria 403 (`wallet_disabled`) para
 * todo mundo, porque nenhuma flag de cobrança está ligada.
 *
 * ## Realm (CU-86akkmepn)
 *
 * A aba corrente vem do `AccountVersionService`, não de leitura de
 * `localStorage`. Na troca de realm o saldo lido vira dado de outra operação:
 * ele sai da tela E do badge do header (`balanceLoaded.emit(null)`), e a
 * consulta se refaz para quem já tinha aberto o modal ao menos uma vez — quem
 * nunca abriu continua sem gastar requisição, que é a razão de o modal ser
 * preguiçoso.
 *
 * Toda consulta passa por um `switchMap`: a resposta em voo do realm anterior é
 * cancelada e não tem como sobrescrever a nova.
 *
 * ## Simulação (09/10/2026, M5 do Misael — CU-17tm466mfzt)
 *
 * O backend lê a carteira do dono da tela (o simulado, quando quem pede pode
 * simular). Entrar ou sair da simulação troca esse dono, e o saldo lido é de
 * outra conta: sai da tela e do badge do header pelo mesmo caminho da troca de
 * realm. O `userChanged` também emite quando os dados do MESMO usuário são
 * atualizados — aí nada muda, por isso a comparação é pelo id do usuário efetivo.
 */
@Component({
  selector: 'app-wallet-modal',
  templateUrl: './wallet-modal.component.html',
  styleUrls: ['./wallet-modal.component.scss'],
})
export class WalletModalComponent implements OnInit, OnDestroy {

  @Input() showModal = new Subject<string>();

  /**
   * O header usa isto só para o badge de saldo negativo. É o que permite não ter
   * requisição eager: o badge aparece depois da primeira abertura.
   */
  @Output() balanceLoaded = new EventEmitter<WalletBalance | null>();

  @ViewChild('walletModal') modal: any;

  modalRef: NgbModalRef;

  balance: WalletBalance = null;
  error: WalletError = null;
  loading = false;
  selectedTab: AccountVersion = NATIONAL;

  subscriptions: Subscription[] = [];

  /**
   * O card arredonda em 2 casas — divergência decidida em relação ao extrato,
   * ver o docblock do `MoneyMicrosPipe`.
   */
  readonly balanceLocale = MONEY_DEFAULT_LOCALE;
  readonly balanceFractionDigits = MONEY_BALANCE_FRACTION_DIGITS;

  /** Já houve uma tentativa de leitura — inclui a que falhou. */
  private loadAttempted = false;

  /** Realm corrente, vindo do stream. `null` até a primeira emissão. */
  private currentVersion: AccountVersion = null;

  /** Id do usuário efetivo (o simulado, quando há simulação) do saldo na tela. */
  private currentOwnerId: string = null;

  /** O modal já foi aberto ao menos uma vez nesta sessão de tela. */
  private hasOpened = false;

  /**
   * `true` pede uma consulta, `false` só cancela a que estiver em voo. O `false`
   * existe porque a troca de realm às vezes não pede nada novo (aba
   * internacional) e mesmo assim a resposta anterior não pode chegar: ela é de
   * outra operação.
   */
  private readonly balanceRequests = new Subject<boolean>();

  get isNationalSelected(): boolean {
    return this.selectedTab === NATIONAL;
  }

  get isDisabled(): boolean {
    return this.error?.kind === 'disabled';
  }

  get isUnavailable(): boolean {
    return this.error?.kind === 'unavailable';
  }

  get hasUnknownError(): boolean {
    return this.error !== null && !this.isDisabled && !this.isUnavailable;
  }

  /** Só anuncia o tempo quando o header `Retry-After` realmente chegou. */
  get retryAfterLabel(): string {
    const seconds = this.error?.retryAfterSeconds;

    return seconds ? `Tente novamente em ${seconds} segundos.` : '';
  }

  constructor(
    private modalService: NgbModal,
    private walletService: WalletService,
    private accountVersionService: AccountVersionService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // O pipeline assina antes do realm: a primeira emissão do BehaviorSubject é
    // síncrona e precisa encontrar quem a atenda.
    this.subscriptions.push(this.watchBalanceRequests());
    this.subscriptions.push(this.showModal.subscribe(() => this.open()));

    this.subscriptions.push(
      this.accountVersionService.getVersion().subscribe((version) => this.applyVersion(version))
    );

    this.currentOwnerId = this.effectiveUserId();
    this.subscriptions.push(
      this.authService.userChanged.subscribe(() => this.applyOwner(this.effectiveUserId()))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.balanceRequests.complete();
  }

  open(): void {
    this.hasOpened = true;

    // Quem está operando na versão internacional abre o modal já na aba dela —
    // a versão corrente é a mesma que pinta o tema do shell.
    this.selectedTab = this.currentVersion === INTERNATIONAL ? INTERNATIONAL : NATIONAL;
    // A classe de versão vive no `<section>` do shell, e o `NgbModal` pendura a
    // janela no `body` — fora dele. Sem levar a classe junto, os tokens do modal
    // caem no `:root` e o botão primário fica verde no escopo internacional.
    this.modalRef = this.modalService.open(this.modal, {
      size: 'md',
      windowClass: this.accountVersionService.themeClass,
    });

    if (this.isNationalSelected) {
      this.loadBalance();
    }
  }

  selectTab(tab: AccountVersion): void {
    this.selectedTab = tab;

    // A carteira internacional não é consultada: o microserviço só conhece as
    // realms `br`/`br_shadow` e recusa moeda diferente de BRL inclusive na
    // leitura. Pedir e mostrar o erro seria anunciar falha onde não há carteira.
    if (tab === NATIONAL && !this.loadAttempted && !this.loading) {
      this.loadBalance();
    }
  }

  selectNational(): void {
    this.selectTab(NATIONAL);
  }

  selectInternational(): void {
    this.selectTab(INTERNATIONAL);
  }

  loadBalance(): void {
    this.loading = true;
    this.loadAttempted = true;
    this.error = null;
    this.balanceRequests.next(true);
  }

  /**
   * Realm novo: o saldo na tela e o badge do header são de outra operação. Os
   * dois somem antes de qualquer consulta nova — badge de saldo negativo do
   * realm anterior é exatamente o "dado velho sem sinal" que esta task existe
   * para tirar do caminho.
   */
  private applyVersion(version: AccountVersion): void {
    const changed = this.currentVersion !== null && this.currentVersion !== version;

    this.currentVersion = version;

    if (!changed) {
      return;
    }

    this.selectedTab = version;
    // A troca de realm vem de um userChanged que pode ter trocado o dono também: registrar
    // aqui evita que o applyOwner do mesmo emit descarte e consulte de novo.
    this.currentOwnerId = this.effectiveUserId();
    this.discardBalance();
  }

  /** Dono novo (entrou ou saiu da simulação): o saldo na tela é de outra conta. */
  private applyOwner(ownerId: string): void {
    if (ownerId === this.currentOwnerId) {
      return;
    }

    this.currentOwnerId = ownerId;
    this.discardBalance();
  }

  private effectiveUserId(): string {
    return this.authService.getUser()?.id ?? null;
  }

  /** Tira o saldo da tela e do badge e, para quem já abriu o modal, consulta de novo. */
  private discardBalance(): void {
    this.balance = null;
    this.error = null;
    this.loading = false;
    this.loadAttempted = false;
    this.balanceLoaded.emit(null);

    // Cancela o que estiver em voo mesmo quando nada novo for pedido: a resposta
    // que chegasse depois seria do realm (ou do dono) anterior.
    this.balanceRequests.next(false);

    // Só refaz a consulta para quem já abriu o modal: a carteira internacional
    // não é consultada (ver `selectTab`) e quem nunca abriu não paga requisição
    // nenhuma.
    if (this.hasOpened && this.isNationalSelected) {
      this.loadBalance();
    }
  }

  private watchBalanceRequests(): Subscription {
    return this.balanceRequests
      .pipe(
        switchMap((shouldFetch) => {
          if (!shouldFetch) {
            return EMPTY;
          }

          return this.walletService.getBalance().pipe(
            map((balance) => ({ balance, error: null as WalletError })),
            // O erro morre dentro do inner observable: deixá-lo subir encerraria
            // o pipeline e o modal pararia de consultar depois do primeiro 503.
            catchError((httpError) =>
              of({ balance: null as WalletBalance, error: classifyWalletError(httpError) })
            )
          );
        })
      )
      .subscribe((result) => {
        // Zero NÃO entra aqui: a carteira que ainda não existe devolve zeros com
        // 200, e trocar indisponibilidade por zero faria o cliente achar que o
        // dinheiro sumiu.
        this.balance = result.balance;
        this.error = result.error;
        this.loading = false;
        this.balanceLoaded.emit(result.balance);
      });
  }
}
